import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { addProductToBookingMutation } from "rsl-api-106/lib/mutations";
import { getBookingProductsQuery } from "rsl-api-106/lib/queries";
import {
  Cart,
  Maybe,
  Mutation,
  MutationAddProductToBookingArgs,
  Product,
  ProductByCategory,
  Query,
  QueryGetBookingProductsArgs,
} from "rsl-api-106/lib/types";

import { useApolloClient } from "@apollo/client";
import LoadingSpinner from "@components/_Layout/Loaders/LoadingSpinner";
import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RadioGroup } from "@headlessui/react";
import { useCart, useConfig, useSession } from "@hooks";
import { sortBy } from "@utils/arrays";
import { getCurrencySymbol } from "@utils/currencies";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from "@utils/localStorage";

type ProductInsuranceProps = {
  selectedInsurance: string | undefined;
  setSelectedInsurance: (value: string) => void;
  title?: JSX.Element;
};
const languagesVarsCodes = [
  "LANG_neobooking_insurance_adding_in_progress", //Ajout de l'assurance annulation en cours...
  "LANG_neobooking_insurance_added_ok", //L'assurance a bien été ajoutée !
  "LANG_neobooking_insurance_more_details", //+ de détails
  "LANG_neobooking_insurance_removing_in_progress", //Suppression de l'assurance annulation en cours...
  "LANG_neobooking_insurance_removed_ok", //Assurance annulation supprimée !
];
const ProductInsurance: React.FC<ProductInsuranceProps> = ({
  selectedInsurance,
  setSelectedInsurance,
  title,
}) => {
  /* Hooks */
  const client = useApolloClient();
  const { sessionId, getLanguageVarValue, lang } = useSession();
  const { cart, setCart } = useCart();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  /* State */
  const [insurances, setInsurances] = useState<ProductByCategory[]>([]);

  const [selectedI, setSelectedI] = useState<string | undefined>(undefined);

  const [loadingInsurances, setLoadingInsurances] = useState<boolean>(true);
  useEffect(() => {
    if (sessionId) {
      //au chargement du composant on récup les deux produits Assurance code
      client
        .query<Query, QueryGetBookingProductsArgs>({
          query: getBookingProductsQuery(),
          variables: {
            session: { name: sessionId },
            input: {
              categoryCodes: ["ASSUR"],
            },
          },
          fetchPolicy: "no-cache",
        })
        .then((response) => {
          if (response.data.getBookingProducts?.bookingProducts) {
            setInsurances(
              response.data.getBookingProducts
                ?.bookingProducts as ProductByCategory[]
            );
          }
        })
        .finally(() => {
          setLoadingInsurances(false);
        });
    }
  }, [sessionId]);
  useEffect(() => {
    //si le selected insurrance n'est pas undefined et n'a pas changé par rapport au localstorage
    if (
      insurances.length > 0 &&
      selectedInsurance !== undefined &&
      !loadingInsurances
    ) {
      //setSelectedI(selectedInsurance);
      handleChangeInsurrance(selectedInsurance);
    }
  }, [insurances]);
  const asyncAddRemoveProduct = async (
    code: string,
    id: number,
    qte: number
  ) => {
    try {
      const data = await client.mutate<
        Mutation,
        MutationAddProductToBookingArgs
      >({
        mutation: addProductToBookingMutation(),
        variables: {
          session: { name: sessionId!! },
          input: {
            productCode: code,
            productID: id,
            quantity: qte,
          },
        },
        fetchPolicy: "no-cache",
      });
      return data.data?.addProductToBooking?.cart;
    } catch (error) {
      //console.error(error);
    }
  };
  const handleChangeInsurrance = async (selected: string) => {
    removeLocalStorageItem("product_insurance");
    let productToAdd: Product | null = null;
    let cart: Maybe<Cart> | undefined = undefined;
    const selectedInsurance = insurances
      .find((x) => x.products)
      ?.products?.find((x) => x?.id.toString() === selected);
    //console.log(insurances);
    //console.log(selectedInsurance);

    let txtInput = getLanguageVarValue(
      "LANG_neobooking_insurance_adding_in_progress"
    );
    let txtOutput = getLanguageVarValue("LANG_neobooking_insurance_added_ok");

    //on exec le code que si le code selected est bien dans la liste des assurances
    if (selectedInsurance) {
      if (selectedInsurance.code.toUpperCase() === "FSA0") {
        txtInput = getLanguageVarValue("LANG_neobooking_update_in_progress");
        txtOutput = getLanguageVarValue("LANG_neobooking_update_done");
      }
      const toastLoadingInsurrance = toast.loading(txtInput);
      try {
        //setSelectedI(selected);
        for (let i = 0; i < insurances.length; i++) {
          if (insurances[i].products?.length ?? 0 > 0) {
            for (let y = 0; y < insurances[i].products?.length!!; y++) {
              const is = insurances[i];
              const pis = is.products;
              const p = pis!![y];
              if (selected !== "" && p?.id === parseInt(selected)) {
                productToAdd = p;
              }
              cart = await asyncAddRemoveProduct(p?.code!!, p?.id!!, 0);
            }
          }
        }
        if (productToAdd && productToAdd !== null) {
          cart = await asyncAddRemoveProduct(
            productToAdd.code!!,
            productToAdd.id!!,
            1
          );
        }

        if (cart && cart !== undefined) {
          setCart(cart!!);
          toast.success(txtOutput, {
            id: toastLoadingInsurrance,
          });
        }
        setLocalStorageItem("product_insurance", selected);
        setSelectedInsurance(selected);
      } catch (error) {
        //setSelectedI(undefined);
      }
    }
  };
  const getInsuranceDetailsUrl = (code: string): string => {
    let url = "";
    switch (code.toUpperCase()) {
      case "FSAI":
        if (lang.toUpperCase() === "FR") {
          url = "https://fr.calameo.com/vacanceole/read/005765862099ff05202b9";
        } else if (lang.toUpperCase() === "AN" || lang.toUpperCase() === "EN") {
          url = "https://fr.calameo.com/vacanceole/read/005765862354842c198c1";
        }
        break;
      case "FSAM":
        if (lang.toUpperCase() === "FR") {
          url = "https://fr.calameo.com/vacanceole/read/005765862ce2f4f2e08e0";
        } else if (lang.toUpperCase() === "AN" || lang.toUpperCase() === "EN") {
          url = "https://fr.calameo.com/vacanceole/read/0057658622a09cfb6ba3f";
        }
        break;
    }
    return url;
  };
  return (
    <>
      {loadingInsurances ? (
        <LoadingSpinner />
      ) : insurances.length > 0 ? (
        <div className="tw-my-4">
          {title ? title : <></>}
          <RadioGroup
            value={
              (cart?.reservations?.[0]?.stays?.[0]?.occupants?.[0]?.lineItems?.filter(
                (x) => ["FSAM", "FSAI", "FSA0"].includes(x?.product?.code ?? "")
              )?.length ?? -1) > 0
                ? cart?.reservations?.[0]?.stays?.[0]?.occupants?.[0]?.lineItems
                    ?.filter((x) =>
                      ["FSAM", "FSAI", "FSA0"].includes(x?.product?.code ?? "")
                    )[0]
                    ?.product?.id.toString()
                : null
            }
            //value={selectedI ? selectedI : null}
            //onChange={setSelectedInsurance}
            onChange={handleChangeInsurrance}
            className="tw-flex tw-flex-col tw-gap-4"
          >
            {insurances.map((x) => {
              return sortBy(x?.products!!, (x) => x?.price?.amount!!)
                ?.reverse()
                .map((product, i) => {
                  return (
                    <RadioGroup.Option
                      as={"div"}
                      key={`insurance_${i}`}
                      value={product?.id.toString()}
                      className={({ active, checked }) =>
                        `${active ? "" : ""}
                    ${
                      checked
                        ? "shadow-custom tw-border-[#707070] tw-text-black"
                        : "tw-border-[#707070]/50"
                    }
                    tw-bg-white tw-relative tw-px-5 tw-py-4 tw-cursor-pointer tw-flex tw-flex-col focus:tw-outline-none tw-rounded-[26px] tw-border tw-border-[#707070]`
                      }
                    >
                      {({ active, checked }) => (
                        <>
                          {product?.code !== "FSA0" && (
                            <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-mb-2">
                              <div className="tw-font-semibold md:tw-text-lg">
                                {product?.label}
                              </div>
                              {product?.price?.amount !== 0 && (
                                <a
                                  onClick={(e) => e.stopPropagation()}
                                  target={"_blank"}
                                  href={getInsuranceDetailsUrl(product?.code!!)}
                                  className={`tw-text-sm md:tw-text-base ${
                                    mode === "vacanceole"
                                      ? "tw-text-red-500 hover:tw-text-red-400"
                                      : "tw-text_corseole-secondary"
                                  } tw-font-bold tw-font-montserrat focus:tw-outline-none`}
                                >
                                  {getLanguageVarValue(
                                    "LANG_neobooking_insurance_more_details"
                                  )}
                                </a>
                              )}
                            </div>
                          )}
                          <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                            <div className="tw-flex tw-items-center">
                              <div className="tw-text-sm">
                                <RadioGroup.Description as="span">
                                  {product?.code === "FSA0" && (
                                    <div className="tw-flex tw-gap-2 tw-items-center">
                                      <div className="tw-font-semibold md:tw-text-lg">
                                        {product?.label}
                                      </div>
                                      {product?.price?.amount !== 0 && (
                                        <a
                                          onClick={(e) => e.stopPropagation()}
                                          target={"_blank"}
                                          href={getInsuranceDetailsUrl(
                                            product?.code!!
                                          )}
                                          className={`${
                                            mode === "vacanceole"
                                              ? "tw-text-red-500 hover:tw-text-red-400"
                                              : "tw-text_corseole-secondary"
                                          } tw-italic tw-font-bold tw-font-dosis focus:tw-outline-none`}
                                        >
                                          {getLanguageVarValue(
                                            "LANG_neobooking_insurance_more_details"
                                          )}
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: product?.description!!,
                                    }}
                                  ></div>
                                </RadioGroup.Description>
                              </div>
                            </div>
                            <div className="tw-flex-shrink-0 tw-text-white tw-flex-none tw-flex tw-min-w-[50px] tw-justify-end">
                              <FontAwesomeIcon
                                className={`${
                                  mode === "vacanceole"
                                    ? "tw-text-primary-800"
                                    : "tw-text-primary_corseole-800"
                                }`}
                                icon={checked ? faCheckCircle : faCircle}
                              />
                            </div>
                          </div>
                          {(product?.price?.amount ?? 0 > 0) ||
                          product?.code === "FSAI" ||
                          product?.code === "FSAM" ? (
                            <div className="tw-flex tw-justify-end tw--mt-6">
                              <span className="md:tw-text-2xl tw-font-bold">
                                {product?.price?.amount}
                                {getCurrencySymbol(
                                  product?.price?.currencyCode!!
                                )}
                              </span>
                            </div>
                          ) : (
                            <></>
                          )}
                        </>
                      )}
                    </RadioGroup.Option>
                  );
                });
            })}
          </RadioGroup>
        </div>
      ) : (
        <></>
      )}
    </>
  );
};
ProductInsurance.propTypes = {
  //
};
export default ProductInsurance;
